import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-work-experience',
  templateUrl: './work-experience.component.html',
  styleUrls: ['./work-experience.component.css'],
})
export class WorkExperienceComponent {
  @Input() formGroup!: FormGroup;
  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    if (!this.formGroup.get('experiences')) {
      this.formGroup.addControl('experiences', this.fb.array([]));
    }

    // Setup watchers for existing experiences
    const experiencesArray = this.formGroup.get('experiences') as FormArray;
    experiencesArray.controls.forEach((control) => {
      this.setupIsCurrentWatcher(control as FormGroup);
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.experiences.updateValueAndValidity();
    }, 100);
  }

  get experiences(): FormArray {
    return this.formGroup.get('experiences') as FormArray;
  }

  addExperience(): void {
    const experienceGroup = this.fb.group({
      title: ['', Validators.required],
      company: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      isCurrent: [false],
      dutiesAchievements: [''],
    });

    this.setupIsCurrentWatcher(experienceGroup);
    this.experiences.push(experienceGroup);
  }

  private setupIsCurrentWatcher(experienceGroup: FormGroup): void {
    experienceGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
      const endDateControl = experienceGroup.get('endDate');
      if (isCurrent) {
        endDateControl?.clearValidators();
        endDateControl?.setValue('');
        endDateControl?.disable();
      } else {
        endDateControl?.enable();
        endDateControl?.setValidators([Validators.required]);
      }
      endDateControl?.updateValueAndValidity();
    });
  }

  removeExperience(index: number): void {
    this.experiences.removeAt(index);
  }

  onNext() {
    // If there are NO experiences at all, allow proceeding (work experience is optional)
    if (this.experiences.length === 0) {
      this.next.emit();
      return;
    } // Mark ALL fields in ALL experiences as touched to show validation errors

    this.experiences.controls.forEach((control) => {
      const group = control as FormGroup;
      Object.keys(group.controls).forEach((key) => {
        const field = group.get(key);
        if (field) {
          field.markAsTouched();
          field.updateValueAndValidity();
        }
      });
    }); // Small delay to ensure DOM updates with error messages

    setTimeout(() => {
      let hasInvalidExperience = false;
      let firstInvalidIndex = -1; // Check each experience for validity

      for (let i = 0; i < this.experiences.length; i++) {
        const group = this.experiences.at(i) as FormGroup;

        const title = group.get('title');
        const company = group.get('company');
        const startDate = group.get('startDate');
        const endDate = group.get('endDate');
        const dutiesAchievements = group.get('dutiesAchievements'); // Check if user started filling this experience (using dutiesAchievements too)

        const hasAnyValue =
          title?.value?.trim() ||
          company?.value?.trim() ||
          startDate?.value ||
          endDate?.value ||
          dutiesAchievements?.value?.trim(); // Included to catch partially filled forms // If ANY field has a value AND the group is invalid, we mark it as invalid experience.

        // The group.invalid check correctly respects the required validators
        // and the conditional disabling of endDate.
        if (hasAnyValue && group.invalid) {
          hasInvalidExperience = true;
          if (firstInvalidIndex === -1) {
            firstInvalidIndex = i;
          }
        }
      } // If there are invalid experiences, scroll to first one and stop

      if (hasInvalidExperience) {
        if (firstInvalidIndex !== -1) {
          const elements = document.querySelectorAll('.card-data'); // Use firstInvalidIndex + 1 because the first element is the "No work experience" message
          const element = elements[firstInvalidIndex + 1];
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return; // Don't proceed to next step
      } // Remove completely empty experiences before proceeding (Logic remains the same)

      for (let i = this.experiences.length - 1; i >= 0; i--) {
        const group = this.experiences.at(i) as FormGroup;
        const value = group.value;
        const hasAnyValue =
          value.title?.trim() ||
          value.company?.trim() ||
          value.startDate ||
          value.endDate ||
          value.dutiesAchievements?.trim();

        if (!hasAnyValue) {
          this.experiences.removeAt(i);
        }
      } // All validations passed, proceed to next step

      this.next.emit();
    }, 100);
  }

  onPrevious() {
    this.previous.emit();
  }

  hasError(index: number, fieldName: string): boolean {
    const control = this.experiences.at(index).get(fieldName);

    if (!control) {
      return false;
    }

    // Don't show error for disabled fields (like endDate when isCurrent is true)
    if (!control.enabled) {
      return false;
    }

    // Show error if field is touched and has errors
    return control.touched && control.invalid;
  }

  getFieldError(index: number, fieldName: string): string | null {
    const control = this.experiences.at(index).get(fieldName);

    if (!control || !control.enabled) {
      return null;
    }

    if (control.touched && control.errors) {
      if (control.errors['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
    }

    return null;
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      title: 'Job Title',
      company: 'Company',
      startDate: 'Start Date',
      endDate: 'End Date',
      dutiesAchievements: 'Duties & Achievements',
    };
    return labels[fieldName] || fieldName;
  }
}
